import React from 'react';
import { Link } from 'gatsby';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import styled from 'styled-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { breakpoints } from '../helpers/breakpoints';
import { flattenNestedArray } from '../helpers/flattenNestedArray';
import { returnLowestPrice } from '../helpers/returnLowestPrice';
import { formatCurrency } from '../helpers/formatCurrency';
import LazyLoad from 'react-lazyload';

import Layout from '../components/layout';
import Main from '../components/main';
import SEO from '../components/seo';
import Button from '../components/button';
import PropertyCard from '../components/property-card';
import Map from '../components/map';
import BackLink from '../components/back-link';
import { StyledFestivalPrice } from '../components/styled-festival-price';
import MusicIcon from '../../static/music-icon.svg';
import VibeIcon from '../../static/vibe-icon.svg';

const StyledFestivalHeader = styled.header`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: var(--v-spacing);
  margin-left: auto;
  margin-right: auto;
  padding-right: var(--gutter);
  max-width: ${breakpoints.mw};
  @media screen and (min-width: ${breakpoints.xl}) {
    padding-right: 0;
    grid-template-columns: 0.7fr 0.3fr;
    grid-gap: var(--gutter);
  }
`;

const StyledFestivalHeaderImagery = styled.div`
  width: 100%;
  height: 25rem;
  overflow: hidden;
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
  @media screen and (min-width: ${breakpoints.md}) {
    height: 35rem;
  }
  @media screen and (min-width: ${breakpoints.xl}) {
    height: 50rem;
  }
`;

const StyledFestivalHeaderProperties = styled.div`
  display: none;
  position: relative;
  @media screen and (min-width: ${breakpoints.xl}) {
    display: flex;
    flex-direction: column;
    background: var(--c-light-grey);
    padding: var(--gutter) var(--gutter) calc(var(--gutter) * 2);
    justify-content: center;
    align-items: center;
  }
`;

const StyledFestivalDetails = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: var(--gutter);
  margin: 3rem auto;
  max-width: ${breakpoints.mw};
  @media screen and (min-width: ${breakpoints.xl}) {
    grid-template-columns: 0.5fr 0.5fr;
    grid-gap: calc(var(--gutter) * 2);
    margin: 4rem auto;
  }
`;

const StyledFestivalMap = styled.div`
  background: var(--c-red);
  height: 30rem;
  overflow: hidden;
  grid-row: 2;
  @media screen and (min-width: ${breakpoints.lg}) {
    height: 50rem;
  }
  @media screen and (min-width: ${breakpoints.xl}) {
    grid-row: auto;
    height: 50vw;
    max-height: 84rem;
    border-radius: 0 50vw 50vw 0;
  }
  .google-map {
    height: 100%;
    width: 100%;
    .google-map__canvas {
      overflow: hidden;
      width: 100%;
      height: 100%;
    }
  }
`;

const StyledFestivalDescription = styled.article`
  padding-left: var(--gutter);
  padding-right: var(--gutter);
  h2 {
    color: var(--c-red);
    margin-bottom: 1rem;
  }
  @media screen and (min-width: ${breakpoints.xl}) {
    padding-left: 0;
    padding-right: calc(var(--gutter) * 2);
  }
`;

const ViewPropertiesButton = styled(Button)`
  position: absolute;
  bottom: -32px;
  background
`;

const ViewPropertiesButtonMobile = styled(Button)`
  @media screen and (min-width: ${breakpoints.xl}) {
    display: none;
  }
`;

export const StyledFestivalDates = styled.p`
  color: var(--c-purple);
  font-weight: 700;
  @media screen and (min-width: ${breakpoints.lg}) {
    font-size: 2.5rem;
    margin-bottom: 3.5rem;
  }
`;

const StyledFestivalTagline = styled.p`
  display: inline-block;
  margin-bottom: 3.2rem;
  @media screen and (min-width: ${breakpoints.lg}) {
    max-width: 75%;
    padding-bottom: 1.8rem;
    border-bottom: solid 2px var(--c-light-grey);
  }
  @media screen and (min-width: ${breakpoints.xl}) {
    max-width: 50%;
  }
`;

const StyledFestivalList = styled.ul`
  display: flex;
  align-items: center;
  img {
    margin-right: 1rem;
  }
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
  li {
    font-size: 1.4rem;
    &:not(:last-of-type) {
      /* margin: 0 0 1rem; */
    }
  }
  &:last-of-type {
    margin-bottom: 2rem;
    @media screen and (min-width: ${breakpoints.lg}) {
      padding-bottom: 2rem;
      border-bottom: solid 2px var(--c-light-grey);
    }
  }
`;

const StyledFestivaSocial = styled.article`
  margin-top: var(--v-spacing);
  display: flex;
  flex-wrap: wrap;
  @media screen and (min-width: ${breakpoints.lg}) {
    padding-top: var(--v-spacing);
    border-top: solid 2px var(--c-light-grey);
  }
`;

// const StyledFestivaSocialFollow = styled(Button)`
//   background: var(--c-white);
//   color: var(--c-black);
//   font-size: 1.4rem;
//   border: solid 1px var(--c-black);
//   margin-left: auto;
//   @media (hover: hover) {
//     &:hover {
//       background: var(--c-grey);
//       color: var(--c-white);
//       border: solid 1px var(--c-white);
//     }
//   }
// `;

const FestivalPage = ({ data }) => {
  const { contentfulFestival: festival } = data;
  const { properties } = festival;
  const featuredProperty = properties[0];
  const { vibe, genre } = festival;
  const description = festival.childContentfulFestivalDescriptionRichTextNode.json;
  const featuredPropertyLowestPrice = returnLowestPrice(featuredProperty.homes.map(home => home.price));
  const homes = flattenNestedArray(properties.map(property => property.homes));
  const lowestPrice = returnLowestPrice(homes.map(home => home.price));
  const ogSocialImage = festival.image.fixed.src.slice(2);
  return (
    <Layout>
      <SEO title={`${festival.name}`} ogImage={ogSocialImage} />
      <StyledFestivalHeader>
        <StyledFestivalHeaderImagery>
          <Img
            fluid={festival.image.fluid}
            alt={`Image of ${festival.name}`}
            style={{
              height: '100%',
            }}
          />
        </StyledFestivalHeaderImagery>
        <StyledFestivalHeaderProperties>
          {featuredProperty ? (
            <PropertyCard
              title={featuredProperty.name}
              imageSrc={featuredProperty.images[0].fluid}
              linkTo={`/festivals/${festival.url}/properties/${featuredProperty.url}`}
              price={featuredPropertyLowestPrice}
              sleeps={featuredProperty.homes
                .map(property => property.sleeps)
                .slice()
                .sort((a, b) => a - b)}
              maxWidth={`32rem`}
              bookingIncentive={featuredProperty.bookingIncentive ? featuredProperty.bookingIncentive : null}
            />
          ) : null}
          <ViewPropertiesButton to={`/festivals/${festival.url}/properties/`} text={'View properties'} />
        </StyledFestivalHeaderProperties>
      </StyledFestivalHeader>
      <Main as={'section'}>
        <BackLink to={'/festivals'} text={'Change festival'} />
        <h1>{festival.name}</h1>
        <StyledFestivalDates>
          {festival.city}, {festival.country} / {festival.startDate} – {festival.endDate}
        </StyledFestivalDates>
        <StyledFestivalTagline>{festival.tagline.childMarkdownRemark.rawMarkdownBody}</StyledFestivalTagline>
        <p>
          From <StyledFestivalPrice>{formatCurrency.format(lowestPrice)}</StyledFestivalPrice> per night
        </p>
        <ViewPropertiesButtonMobile to={`/festivals/${festival.url}/properties/`} text={'View properties'} />
      </Main>
      <StyledFestivalDetails>
        <StyledFestivalMap>
          <LazyLoad height={'100%'}>
            <Map
              festival={festival.location}
              properties={properties}
              googleMapURL="https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=AIzaSyDOh7fi5MO3Vxj7L7PY0oe3AwAApjT8iGA"
              loadingElement={<div />}
              containerElement={<div className="google-map" />}
              mapElement={<div className="google-map__canvas" />}
            />
          </LazyLoad>
        </StyledFestivalMap>
        <StyledFestivalDescription>
          <h2>Festival Info</h2>
          {genre ? (
            <StyledFestivalList>
              <img src={MusicIcon} width={36} alt="Created by habione 404 from the Noun Project" />
              {genre.map((genre, index) => {
                return <li key={genre}>{(index ? ', ' : '') + genre}</li>;
              })}
            </StyledFestivalList>
          ) : null}
          {vibe ? (
            <StyledFestivalList>
              <img src={VibeIcon} width={36} alt="Created by Cezary Lopacinski from the Noun Project" />
              {vibe.map((vibe, index) => {
                return <li key={vibe}>{(index ? ', ' : '') + vibe}</li>;
              })}
            </StyledFestivalList>
          ) : null}
          {documentToReactComponents(description)}
          <StyledFestivaSocial></StyledFestivaSocial>
        </StyledFestivalDescription>
      </StyledFestivalDetails>
    </Layout>
  );
};

export default FestivalPage;

export const festivalPageQuery = graphql`
  query($url: String!) {
    contentfulFestival(url: { eq: $url }) {
      name
      url
      image {
        fluid(maxWidth: 1156, quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
        fixed(width: 1200, height: 630) {
          src
        }
      }
      childContentfulFestivalDescriptionRichTextNode {
        json
      }
      tagline {
        childMarkdownRemark {
          rawMarkdownBody
        }
      }
      startDate(formatString: "Do MMM")
      endDate(formatString: "Do MMM")
      genre
      vibe
      city
      country
      location {
        lon
        lat
      }
      properties {
        id
        name
        url
        bookingIncentive
        homes {
          name
          price
          sleeps
        }
        images {
          fluid(maxWidth: 320, maxHeight: 160, quality: 80) {
            ...GatsbyContentfulFluid_withWebp
          }
        }
        location {
          lon
          lat
        }
      }
    }
  }
`;
